import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import { User, InfoCircle } from 'iconsax-react';
import { TabellaListaServer } from '../../../../components/Tabella/TabellaListaServer';
import { ChipFilter } from '../../../../components/Tabella/ChipFilter';
import { RangeFilterButton } from '../../../../components/Tabella/RangeFilterButton';
import MyApiClient from '../../../../../api-client/MyApiClient';

const PAGE_SIZE = 20;

// La ricerca la tiene la tabella (casella accanto ai filtri): qui solo il resto.
const FILTRI_INIZIALI = {
	dateFrom: '',
	dateTo: '',
	paymentStatus: '',
	bookStatus: '',
	expiring: '',
	// Registro o Archivio (conservazione 10 anni), come nel registro tesserati (21/10/2026)
	archivio: ''
};

// Etichette e colori delle pill, come da mockup del libro soci
// Colori del prototipo, via le classi `sid-pill` definite in style.css.
const STATO_LIBRO = {
	ACTIVE: { label: 'Attivo', cls: 'sid-pill--green' },
	RESIGNED: { label: 'Dimesso', cls: 'sid-pill--gray' },
	EXPELLED: { label: 'Espulso', cls: 'sid-pill--red' }
};

// Valori dei filtri a chip, quelli che il server riconosce.
const PAGAMENTO_OPTIONS = [
	{ value: 'PAID', label: 'Pagato' },
	{ value: 'UNPAID', label: 'Non pagato' }
];

const STATO_OPTIONS = Object.entries(STATO_LIBRO).map(([value, { label }]) => ({ value, label }));

/*
 * Sollecito manuale della quota (TASK #4 e #10, 21/10/2026). «In scadenza» = nei prossimi 30 giorni; «Scaduta» = negli
 * ultimi 12 mesi, per non scrivere a chi ha lasciato la quota scadere da anni. L'avviso automatico della quota è spento
 * (TASK #10 da chiarire): il sollecito lo manda la struttura quando vuole.
 * Il server esclude dimessi ed espulsi.
 */
const QUOTA_OPTIONS = [
	{ value: 'in_scadenza', label: 'In scadenza' },
	{ value: 'scaduta', label: 'Scaduta' }
];

const giornoLocale = (giorni) => {
	const d = new Date();
	d.setDate(d.getDate() + giorni);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const intervalloQuota = (valore) => {
	if (valore === 'in_scadenza') return { expiryFrom: giornoLocale(0), expiryTo: giornoLocale(30) };
	if (valore === 'scaduta') return { expiryFrom: giornoLocale(-365), expiryTo: giornoLocale(-1) };
	return {};
};

const DESCRIZIONE_QUOTA = {
	in_scadenza: 'con la quota in scadenza nei prossimi 30 giorni',
	scaduta: 'con la quota scaduta negli ultimi 12 mesi'
};

const TIPOLOGIA = {
	MEMBER: 'Socio',
	DIRECTOR: 'Consiglio direttivo'
};

// Cerchio da 28px come nel mockup, ma con la foto del profilo quando c'è —
// stesso comportamento della lista player. Il segnaposto arancione resta il
// ripiego per chi la foto non ce l'ha.
const AvatarSocio = ({ imageUrl, name }) => (
	<span
		className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 overflow-hidden"
		style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(253, 126, 20, 0.12)', color: '#fd7e14' }}
	>
		{imageUrl
			? <img src={imageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
			: <User size="15" variant="Linear" />}
	</span>
);

/**
 * Il socio ammesso a cui la quota non e' ancora stata venduta.
 *
 * Si diventa soci con la delibera: chi l'assemblea ha ammesso entra nel libro
 * subito, ma senza periodo, perche' quello lo fissa l'ordine. Finche' non c'e'
 * un ordine collegato la posizione resta aperta, e alla segreteria manca un
 * passaggio: vendere l'abbonamento socio.
 *
 * Diverso da una quota semplicemente scaduta o non incassata, che un ordine ce
 * l'ha: quella si sollecita, questa si vende.
 */
const quotaDaVendere = (riga) => !riga.orderId && !riga.dateStart;

const formatDate = (iso) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (isNaN(d.getTime())) return iso;
	return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const SociTable = () => {

	const [data_json, setDataJson] = useState([]);
	const [pageCount, setPageCount] = useState(0);
	const [total, setTotal] = useState(0);

	const [filtri, setFiltri] = useState(FILTRI_INIZIALI);
	const [ricercaApplicata, setRicercaApplicata] = useState('');
	const [selezionati, setSelezionati] = useState([]);

	const [confirmShowed, setConfirmShowed] = useState(false);
	const [onContinue, setOnContinue] = useState(null);
	const [confirmText, setConfirmText] = useState('');

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const [loadingShowed, setLoadingShowed] = useState(false);


	const paginazione = useRef({ pageIndex: 0, pageSize: PAGE_SIZE });
	// Solo l'ultima richiesta conta: filtri ricordati o cambiati in fretta fanno partire più chiamate,
	// e una risposta vecchia arrivata dopo mostrava la lista senza filtri con i chip accesi.
	const ultimaRichiesta = useRef(0);
	const navigate = useNavigate();

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	// La ricerca arriva dalla tabella gia' ritardata (600ms) e solo da 3 caratteri
	// in su; i filtri arrivano come `externalFilters` e, quando cambiano, la tabella
	// torna da sola alla prima pagina.
	const caricaDati = useCallback(async (pageIndex, pageSize, search, filtriApplicati) => {
		const auth = getAuth();
		if (!auth) return;
		const richiesta = ++ultimaRichiesta.current;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.getMemberBook(auth.token, {
			offset: pageIndex * pageSize,
			limit: pageSize,
			search,
			dateFrom: filtriApplicati.dateFrom,
			dateTo: filtriApplicati.dateTo,
			paymentStatus: filtriApplicati.paymentStatus,
			bookStatus: filtriApplicati.bookStatus,
			// Il filtro «Quota» vale solo nel registro: in archivio non si vede e non filtra
			...(filtriApplicati.archivio !== 'si' && intervalloQuota(filtriApplicati.expiring)),
			...(filtriApplicati.archivio === 'si' && { archived: true })
		});
		if (richiesta !== ultimaRichiesta.current) return;
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			mostraErrore("Non è stato possibile caricare il libro soci: " + (risultato ? risultato.error : 'errore generico'));
			return;
		}

		const righe = (risultato.items || []).map(element => ({
			id: element.userId,
			membershipId: element.id,
			imageUrl: element.picture && element.picture.uri ? element.picture.uri : null,
			memberNumber: element.memberNumber,
			name: element.name,
			level: element.level,
			membershipRank: element.membershipRank,
			dateStart: element.dateStart,
			dateEnd: element.dateEnd,
			bookStatus: element.bookStatus,
			bookStatusDate: element.bookStatusDate,
			paymentStatus: element.paymentStatus,
			orderId: element.orderId,
			source: element.source,
			createdAt: element.createdAt,
			original: element
		}));

		setDataJson(righe);
		// La selezione vale per i soci che si vedono: cambiando pagina o filtri si riparte da zero
		setSelezionati([]);
		setTotal(risultato.total || 0);
		setPageCount(Math.ceil((risultato.total || 0) / pageSize));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchData = useCallback(({ pageIndex, pageSize, globalFilter, externalFilters }) => {
		const search = (globalFilter || '').trim();
		paginazione.current = { pageIndex, pageSize };
		setRicercaApplicata(search);
		caricaDati(pageIndex, pageSize, search, externalFilters || FILTRI_INIZIALI);
	}, [caricaDati]);

	const aggiornaFiltro = (campo, valore) => setFiltri(prec => ({ ...prec, [campo]: valore }));

	// Quanti, fra quelli a schermo, aspettano ancora la vendita della quota.
	const inAttesaDiQuota = data_json.filter(quotaDaVendere).length;

	// Il sollecito si fa dal filtro «Quota» (in scadenza o scaduta), nel registro: solo lì compaiono caselle e tasto
	const sollecitoPossibile = !!filtri.expiring && filtri.archivio !== 'si';

	const toggleSelezione = (membershipId) => {
		setSelezionati(prec => prec.includes(membershipId)
			? prec.filter(x => x !== membershipId)
			: [...prec, membershipId]);
	};

	const idsInPagina = data_json.map(r => r.membershipId);
	const tuttiInPagina = idsInPagina.length > 0 && idsInPagina.every(id => selezionati.includes(id));

	const colonnaSelezione = {
		id: 'selezione',
		Header: () => (
			<Form.Check
				type="checkbox"
				aria-label="Seleziona tutti i soci della pagina"
				checked={tuttiInPagina}
				onChange={() => setSelezionati(tuttiInPagina ? [] : idsInPagina)}
			/>
		),
		Cell: ({ row }) => (
			<Form.Check
				type="checkbox"
				aria-label={`Seleziona ${row.original.name}`}
				checked={selezionati.includes(row.original.membershipId)}
				onChange={() => toggleSelezione(row.original.membershipId)}
			/>
		),
		disableFilters: true
	};

	const data_column = [
		...(sollecitoPossibile ? [colonnaSelezione] : []),
		{
			Header: 'N.',
			accessor: 'memberNumber',
			Cell: ({ row }) => (
				<span className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
					{row.original.memberNumber ? String(row.original.memberNumber).padStart(3, '0') : '—'}
				</span>
			),
			disableFilters: true
		},
		{
			Header: 'Nome e Cognome',
			accessor: 'name',
			Cell: ({ row }) => (
				<div className="d-flex align-items-center">
					<AvatarSocio imageUrl={row.original.imageUrl} name={row.original.name} />
					<span className="me-2">{row.original.name}</span>
					{row.original.level && (
						<span className={`sid-tag ${row.original.level === 'JUNIOR' ? 'sid-tag--blue' : 'sid-tag--orange'}`}>
							{row.original.level === 'JUNIOR' ? 'Junior' : 'Senior'}
						</span>
					)}
				</div>
			),
			disableFilters: true
		},
		{
			Header: 'Tipologia',
			accessor: 'membershipRank',
			Cell: ({ row }) => TIPOLOGIA[row.original.membershipRank] || row.original.membershipRank,
			disableFilters: true
		},
		{
			Header: 'Data entrata',
			accessor: 'dateStart',
			Cell: ({ row }) => formatDate(row.original.dateStart),
			disableFilters: true
		},
		{
			Header: 'Data uscita',
			accessor: 'dateEnd',
			Cell: ({ row }) => formatDate(row.original.dateEnd),
			disableFilters: true
		},
		{
			Header: 'Pagamento',
			accessor: 'paymentStatus',
			Cell: ({ row }) => {
				// Tre stati, non due: pagato, da incassare, e da vendere. L'ultimo e'
				// un lavoro che manca alla segreteria, non un socio in ritardo.
				if (quotaDaVendere(row.original)) {
					return <span className="sid-pill sid-pill--blue">Quota da vendere</span>;
				}
				return (
					<span className={`sid-pill ${row.original.paymentStatus === 'PAID' ? 'sid-pill--green' : 'sid-pill--amber'}`}>
						{row.original.paymentStatus === 'PAID' ? 'Pagato' : 'Non pagato'}
					</span>
				);
			},
			disableFilters: true
		},
		{
			Header: 'Stato libro soci',
			accessor: 'bookStatus',
			Cell: ({ row }) => {
				const stato = STATO_LIBRO[row.original.bookStatus] || STATO_LIBRO.ACTIVE;
				return (
					<span className={`sid-pill ${stato.cls}`}>
						{stato.label}{row.original.bookStatusDate ? ` dal ${formatDate(row.original.bookStatusDate)}` : ''}
					</span>
				);
			},
			disableFilters: true
		},
	];

	const apriScheda = (row) => {
		navigate('/struttura/soci/scheda/' + row.membershipId, { state: { inArchivio: filtri.archivio === 'si' } });
	};

	// «Visualizza player» e «Vendi quota» stanno nella scheda del socio (03/10/2026)

	const esporta = async () => {
		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const blob = await apiClient.exportMemberBook(auth.token, {
			search: ricercaApplicata,
			dateFrom: filtri.dateFrom,
			dateTo: filtri.dateTo,
			paymentStatus: filtri.paymentStatus,
			bookStatus: filtri.bookStatus,
			...(filtri.archivio !== 'si' && intervalloQuota(filtri.expiring)),
			...(filtri.archivio === 'si' && { archived: true })
		});
		setLoadingShowed(false);

		if (!blob || blob.error) {
			mostraErrore("Non è stato possibile generare l'export.");
			return;
		}

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'libro-soci.xlsx');
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	const chiediSollecito = () => {
		const aTutti = selezionati.length === 0;
		const quanti = aTutti ? total : selezionati.length;
		setOnContinue(() => () => inviaSollecito(aTutti));
		setConfirmText(aTutti
			? `Il sollecito va a <b>${quanti === 1 ? 'un socio' : `tutti i ${quanti} soci`} ${DESCRIZIONE_QUOTA[filtri.expiring]}</b>${ricercaApplicata ? ' trovati con la ricerca' : ''}, per email (per i junior al genitore).`
			: `Il sollecito va a <b>${quanti === 1 ? 'un socio selezionato' : `${quanti} soci selezionati`}</b>, per email (per i junior al genitore).`);
		setConfirmShowed(true);
	};

	// Tutti i soci del filtro, pagina dopo pagina: il server riceve l'elenco esatto di chi si vede nella lista
	const tuttiDelFiltro = async (apiClient, token) => {
		const ids = [];
		for (let offset = 0; ; offset += 200) {
			const pagina = await apiClient.getMemberBook(token, {
				offset,
				limit: 200,
				search: ricercaApplicata,
				dateFrom: filtri.dateFrom,
				dateTo: filtri.dateTo,
				paymentStatus: filtri.paymentStatus,
				bookStatus: filtri.bookStatus,
				...intervalloQuota(filtri.expiring)
			});
			if (!pagina || pagina.error) return null;
			(pagina.items || []).forEach(m => ids.push(m.id));
			if (!pagina.items?.length || ids.length >= (pagina.total || 0)) return ids;
		}
	};

	const inviaSollecito = async (aTutti) => {
		setConfirmShowed(false);
		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const membershipIds = aTutti ? await tuttiDelFiltro(apiClient, auth.token) : selezionati;
		if (!membershipIds) {
			setLoadingShowed(false);
			mostraErrore("Non è stato possibile caricare l'elenco dei soci da sollecitare.");
			return;
		}
		if (membershipIds.length === 0) {
			setLoadingShowed(false);
			mostraErrore('Nessun socio da sollecitare con questi filtri.');
			return;
		}
		const risultato = await apiClient.sendMemberExpiryNotifications(auth.token, { membershipIds });
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			mostraErrore("Invio non riuscito: " + (risultato ? risultato.error : 'errore generico'));
			return;
		}

		setModalTitle('Sollecito inviato');
		setModalText(`Email inviate: ${risultato.sent}. Non riuscite: ${risultato.failed}. Senza email: ${risultato.skipped}.`);
		setModalShowed(true);
		setSelezionati([]);
	};





	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={confirmShowed} centered>
				<Modal.Header>
					<Modal.Title>Inviare il sollecito?</Modal.Title>
					<Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64" dangerouslySetInnerHTML={{ __html: confirmText }}></Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center sid-footer-conferma">
					<Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>Annulla</Button>
					<Button className="btn btn-primary btn-add" onClick={() => { setConfirmShowed(false); if (onContinue) onContinue(); }}>Invia</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64">{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<TabellaListaServer
				openColumn={'name'}
				openRow={(row) => apriScheda(row)}
				pageList={true}
				cardTitle={'Soci'}
				sidLayout={true}
				disableSortBy={true}
				headerActions={
					<div className="d-flex gap-2 flex-wrap">
						{/* Sollecito della quota: solo con il filtro «Quota» e qualcuno in lista (un tasto inutilizzabile non si mostra) */}
						{sollecitoPossibile && total > 0 && (
							<Button className="btn btn-add sid-btn-secondary" onClick={chiediSollecito}>
								{selezionati.length > 0 ? `Sollecita i selezionati (${selezionati.length})` : 'Sollecita tutti'}
							</Button>
						)}
						<Button className="btn btn-add sid-btn-secondary" onClick={esporta}>Esporta</Button>
						<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={() => navigate('/struttura/soci/aggiungi')}>+ Aggiungi socio</Button>
					</div>
				}
				filterBar={
					<>
						{inAttesaDiQuota > 0 && (
							<div className="sid-info-banner">
								<InfoCircle size="18" className="sid-info-icon" />
								<div>
									{inAttesaDiQuota === 1
										? "Un socio e' stato ammesso ma la quota non gli e' ancora stata venduta."
										: `${inAttesaDiQuota} soci sono stati ammessi ma la quota non e' ancora stata loro venduta.`}
									{' '}Finche' non c'e' un ordine pagato la loro posizione resta senza periodo: usa
									l'icona della borsa sulla riga per vendere l'abbonamento socio.
								</div>
							</div>
						)}
					</>
				}
				filterControl={
					<div className="sid-chip-filters">
						<RangeFilterButton
							label="Periodo"
							ranges={[{ key: 'periodo', label: 'Data di iscrizione', type: 'date' }]}
							value={{ periodo: { da: filtri.dateFrom, a: filtri.dateTo } }}
							onApply={(nuovi) => setFiltri(prec => ({ ...prec, dateFrom: nuovi.periodo?.da || '', dateTo: nuovi.periodo?.a || '' }))}
						/>
						<ChipFilter label="Pagamento" options={PAGAMENTO_OPTIONS} value={filtri.paymentStatus} onChange={(valore) => aggiornaFiltro('paymentStatus', valore)} />
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={filtri.bookStatus} onChange={(valore) => aggiornaFiltro('bookStatus', valore)} />
						{filtri.archivio !== 'si' && (
							<ChipFilter label="Quota" options={QUOTA_OPTIONS} value={filtri.expiring} onChange={(valore) => aggiornaFiltro('expiring', valore)} />
						)}
						<Form.Select value={filtri.archivio} onChange={(e) => aggiornaFiltro('archivio', e.target.value)} style={{ width: 'auto' }}>
							<option value="">Registro</option>
							<option value="si">Archivio</option>
						</Form.Select>
					</div>
				}
				externalFilters={filtri}
				totalItems={total}
				hasSearch={true}
				searchPlaceholder={'Cerca per nome o cognome'}
				hasFilter={false}
				hasAddMore={false}
				data_column={data_column}
				data_json={data_json}
				manualPagination={true}
				pageCount={pageCount}
				fetchData={fetchData}
				sort_by={'memberNumber'}
				sort_type={'asc'}
				manualGlobalFilter={true}
				manualFilters={false}
			/>
		</>
	)
}

export default SociTable;
